import { type Skin, SkinSchema } from './schema.js';

/**
 * Skin packs: a folder (or a set of files picked together) with a `skin.json` at its root plus the
 * asset files the manifest references by relative key (`cards/geralt.webp`, `board.webp`, ...).
 *
 * The parser is pure: it takes file descriptors, returns the validated skin and the assets it
 * references, and never touches the DOM or storage. Unknown files are ignored so a pack can carry a
 * README, previews or source files.
 */

/** Anything blob-shaped: a browser `File`/`Blob`, Node's `Blob`, or a test double. */
export interface PackBlob {
  readonly size: number;
  readonly type: string;
  text(): Promise<string>;
}

export interface PackFile<B extends PackBlob = PackBlob> {
  /** Path as picked: `webkitRelativePath` for folders, the file name for loose files. */
  path: string;
  blob?: B;
  /** Already-read text (`skin.json` from a text editor, tests). Takes precedence over `blob`. */
  text?: string;
}

export interface SkinPack<B extends PackBlob = PackBlob> {
  skin: Skin;
  /** Asset key (as written in the manifest) → file data. Only referenced assets are kept. */
  assets: Map<string, B>;
}

export type SkinPackErrorCode =
  | 'no_manifest'
  | 'multiple_manifests'
  | 'unreadable_manifest'
  | 'invalid_json'
  | 'invalid_skin'
  | 'missing_assets';

export class SkinPackError extends Error {
  override readonly name = 'SkinPackError';
  constructor(
    readonly code: SkinPackErrorCode,
    message: string,
    /** Asset keys or schema paths involved, for the UI to list. */
    readonly details: readonly string[] = [],
  ) {
    super(message);
  }
}

export const SKIN_MANIFEST_NAME = 'skin.json';

/** Forward slashes, no leading `./` or `/`, no empty segments. */
export function normalizePackPath(path: string): string {
  return path
    .replace(/\\/g, '/')
    .split('/')
    .filter((seg) => seg !== '' && seg !== '.')
    .join('/');
}

/** Every asset key a skin references, in manifest order, de-duplicated. */
export function skinAssetKeys(skin: Pick<Skin, 'assets'>): string[] {
  const assets = skin.assets;
  if (!assets) return [];
  const keys: string[] = [];
  if (assets.board) keys.push(assets.board);
  if (assets.cardBack) keys.push(assets.cardBack);
  for (const group of [assets.frames, assets.rowIcons, assets.abilityIcons, assets.weather, assets.cards]) {
    if (!group) continue;
    for (const key of Object.values(group)) keys.push(key);
  }
  return [...new Set(keys)];
}

function formatIssues(error: unknown): string[] {
  if (typeof error !== 'object' || error === null || !('issues' in error)) return [String(error)];
  const issues = (error as { issues: Array<{ path: PropertyKey[]; message: string }> }).issues;
  return issues.map((i) => `${i.path.map(String).join('.') || '(root)'}: ${i.message}`);
}

/**
 * Parses a picked pack. Resolves the pack root from where `skin.json` sits (so a folder picked with
 * its parent still works), validates the manifest with `SkinSchema`, and collects every referenced
 * asset relative to that root. Throws `SkinPackError` with a message a user can act on.
 */
export async function parseSkinPack<B extends PackBlob>(
  files: ReadonlyArray<PackFile<B>>,
): Promise<SkinPack<B>> {
  const entries = files.map((f) => ({ file: f, path: normalizePackPath(f.path) }));

  const manifests = entries.filter(
    (e) => e.path === SKIN_MANIFEST_NAME || e.path.endsWith(`/${SKIN_MANIFEST_NAME}`),
  );
  if (manifests.length === 0) {
    throw new SkinPackError(
      'no_manifest',
      `No ${SKIN_MANIFEST_NAME} found. A skin pack is a folder with ${SKIN_MANIFEST_NAME} at its root.`,
    );
  }
  // Shallowest manifest wins; two at the same depth is ambiguous.
  const depth = (p: string) => p.split('/').length;
  manifests.sort((a, b) => depth(a.path) - depth(b.path));
  const first = manifests[0];
  if (!first) throw new SkinPackError('no_manifest', `No ${SKIN_MANIFEST_NAME} found.`);
  const second = manifests[1];
  if (second && depth(second.path) === depth(first.path)) {
    throw new SkinPackError(
      'multiple_manifests',
      `Found more than one ${SKIN_MANIFEST_NAME} (${first.path}, ${second.path}). Pick one skin folder at a time.`,
      manifests.map((m) => m.path),
    );
  }
  const root = first.path.slice(0, first.path.length - SKIN_MANIFEST_NAME.length); // '' or 'dir/'

  let text: string;
  if (typeof first.file.text === 'string') text = first.file.text;
  else if (first.file.blob) {
    try {
      text = await first.file.blob.text();
    } catch (cause) {
      throw new SkinPackError(
        'unreadable_manifest',
        `Could not read ${first.path}: ${cause instanceof Error ? cause.message : String(cause)}`,
      );
    }
  } else throw new SkinPackError('unreadable_manifest', `${first.path} has no content.`);

  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch (cause) {
    throw new SkinPackError(
      'invalid_json',
      `${first.path} is not valid JSON: ${cause instanceof Error ? cause.message : String(cause)}`,
    );
  }

  const parsed = SkinSchema.safeParse(json);
  if (!parsed.success) {
    const issues = formatIssues(parsed.error);
    throw new SkinPackError(
      'invalid_skin',
      `${first.path} is not a valid skin: ${issues.slice(0, 5).join('; ')}${issues.length > 5 ? '; …' : ''}`,
      issues,
    );
  }
  const skin = parsed.data;

  const byRelativePath = new Map<string, PackFile<B>>();
  for (const e of entries) {
    if (e === first || !e.path.startsWith(root)) continue;
    byRelativePath.set(e.path.slice(root.length), e.file);
  }

  const assets = new Map<string, B>();
  const missing: string[] = [];
  for (const key of skinAssetKeys(skin)) {
    const file = byRelativePath.get(normalizePackPath(key));
    if (file?.blob) assets.set(key, file.blob);
    else missing.push(key);
  }
  if (missing.length > 0) {
    throw new SkinPackError(
      'missing_assets',
      `${SKIN_MANIFEST_NAME} references ${missing.length} file${missing.length === 1 ? '' : 's'} that ${
        missing.length === 1 ? 'is' : 'are'
      } not in the pack: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? ', …' : ''}`,
      missing,
    );
  }

  return { skin, assets };
}

/** A minimal, valid `skin.json` users can copy and edit. Every section is optional in a real skin. */
export function exampleSkinJson(): string {
  const example: Skin = {
    id: 'my-skin',
    name: 'My skin',
    version: 1,
    colors: {
      ui: { board: '#1f2a1a', panel: '#141a10', accent: '#c9d36a' },
      factions: { northern_realms: { primary: '#274a6d', frame: '#9fc3e6' } },
      hero: { glow: '#fff1a8' },
    },
    fonts: { display: "'Cinzel', Georgia, serif" },
    assets: {
      board: 'board.webp',
      cardBack: 'card-back.webp',
      cards: { geralt_of_rivia: 'cards/geralt.webp', biting_frost: 'cards/frost.webp' },
    },
    strings: { cards: { geralt_of_rivia: { name: 'The White Wolf' } } },
  };
  return JSON.stringify(SkinSchema.parse(example), null, 2);
}
